import { useAtom, useAtomValue } from 'jotai';
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { activationKeyOptionsAtom } from 'views/Admin/Dashboard/CADashboard/CAList/state';

import {
  SourceType,
  sourceTypeAtom,
  sourceTypeConfig,
  useOnSourceTypeChangeFindingsResetAtoms,
  agentKeyAtom,
} from './state';

const options = [
  {
    value: SourceType.ALL,
    label: 'All Asset Types',
    icon: 'fas fa-th-list',
    level: 0,
  },
  {
    value: SourceType.WAS,
    label: 'Web Apps',
    icon: sourceTypeConfig[SourceType.WAS].iconClass,
    level: 0,
  },
  {
    value: SourceType.SAST,
    label: 'SAST',
    icon: sourceTypeConfig[SourceType.SAST].iconClass,
    level: 0,
  },
  {
    value: SourceType.EP,
    label: 'Endpoints - All',
    icon: sourceTypeConfig[SourceType.EP].iconClass,
    level: 0,
  },
  {
    value: SourceType.EP_EXTERNAL,
    label: 'Endpoints - External',
    icon: 'fas fa-external-link-alt',
    level: 0,
  },
  {
    value: SourceType.EP_INTERNAL,
    label: 'Endpoints - Internal',
    icon: sourceTypeConfig[SourceType.EP_INTERNAL].iconClass,
    level: 0,
  },
  {
    value: SourceType.EP_EC2,
    label: 'Endpoints - AWS',
    icon: sourceTypeConfig[SourceType.EP_EC2].iconClass,
    level: 0,
  },
];

const caHostOptions = [
  {
    value: SourceType.CA,
    label: 'Cloud Agents - All',
    icon: sourceTypeConfig[SourceType.CA].iconClass,
    level: 0,
  },
  {
    value: SourceType.CA_SERVER,
    label: 'Cloud Agents - Servers',
    icon: sourceTypeConfig[SourceType.CA_SERVER].iconClass,
    level: 0,
  },
  {
    value: SourceType.CA_WORKSTATIONS,
    label: 'Cloud Agents - Workstations',
    icon: sourceTypeConfig[SourceType.CA_WORKSTATIONS].iconClass,
    level: 0,
  },
];

const HostTypeDropdown = () => {
  const [sourceType, setSourceType] = useAtom(sourceTypeAtom);
  const [agentKey, setAgentKey] = useAtom(agentKeyAtom);
  const resetSourceTypeChangeFindingsResetAtoms =
    useOnSourceTypeChangeFindingsResetAtoms();
  const agentActivationKeysOptions = useAtomValue(activationKeyOptionsAtom);
  // Add state to track the selected option ID
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hostTypeOptions, setHostTypeOptions] = useState<
    {
      level: number;
      icon: string;
      value: string;
      label: string;
      agentKey?: string;
      id: string;
    }[]
  >([]);

  useEffect(() => {
    const fetchHostOptions = async () => {
      const formattedAgentKeys = agentActivationKeysOptions?.map(
        (option, _index) => ({
          ...option,
          value: option.label.toLowerCase().includes('workstation')
            ? SourceType.CA_WORKSTATIONS
            : SourceType.CA_SERVER,
          level: 1, // Indentation level
          icon: option.label.toLowerCase().includes('workstation')
            ? sourceTypeConfig[SourceType.CA_WORKSTATIONS].iconClass
            : sourceTypeConfig[SourceType.CA_SERVER].iconClass, // Icon for each option
          agentKey: option.value,
          id: `agent-${option.value}`, // Add unique ID
        }),
      );

      // Filter options containing "Servers" and "Workstations"
      const workstationOptions = formattedAgentKeys?.filter((opt) =>
        opt.label.toLowerCase().includes('workstation'),
      );
      const serverOptions = formattedAgentKeys?.filter(
        (opt) => !opt.label.toLowerCase().includes('workstation'),
      );

      // Add unique IDs to base options
      const baseOptions = options.map((opt) => ({
        ...opt,
        id: `base-${opt.value}`,
      }));

      // Add unique IDs to CA host options
      const caOptions = caHostOptions.map((opt) => ({
        ...opt,
        id: `ca-${opt.value}`,
      }));

      // Merge the lists in the correct order
      const newHostTypeOptions = [
        ...baseOptions, // "All Host Types"
        caOptions[0], // "Cloud Agents - All"
        caOptions[1], // "CA All Servers"
        ...(serverOptions || []), // Add all "Servers" options here
        caOptions[2], // "CA All Workstations"
        ...(workstationOptions || []), // Add all "Workstations" options here
      ];
      setHostTypeOptions(newHostTypeOptions);
    };

    fetchHostOptions();
  }, [agentActivationKeysOptions]);

  // Initialize selected ID when options change
  useEffect(() => {
    if (hostTypeOptions.length > 0) {
      // Find the current option based on sourceType and agentKey
      const currentOption =
        agentKey !== ''
          ? hostTypeOptions.find(
              (option) =>
                option.value === sourceType && option.agentKey === agentKey,
            )
          : hostTypeOptions.find((option) => option.value === sourceType);

      if (currentOption) {
        setSelectedId(currentOption.id);
      }
    }
  }, [hostTypeOptions, sourceType, agentKey]);

  const colourStyles = {
    control: (styles) => ({
      ...styles,
      backgroundColor: 'white',
    }),
    option: (styles, { isDisabled, isFocused, isSelected }) => {
      return {
        ...styles,
        backgroundColor: isDisabled
          ? undefined
          : isSelected
          ? '#007bff' // Bright blue for selected item
          : isFocused
          ? '#f0f0f0' // Light gray for hover
          : undefined,
        color: isSelected ? 'white' : undefined, // White text for selected item
        cursor: isDisabled ? 'not-allowed' : 'pointer', // Pointer cursor on hover
        display: 'flex',
        alignItems: 'center',
        transition: 'background-color 0.2s ease', // Smooth transition for hover effect
        '&:active': {
          backgroundColor: '#0069d9', // Darker blue when clicked
        },
      };
    },
    singleValue: (styles) => ({
      ...styles,
      display: 'flex',
      alignItems: 'center',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      verticalAlign: 'middle',
    }),
  };

  const customSingleValue = ({ data }) => (
    <div
      style={{
        display: 'block',
        gridColumn: '1 / 1',
        gridRow: '1 / 1',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        verticalAlign: 'middle',
      }}
    >
      <i className={data.icon} style={{ marginRight: 5 }} />
      {data.label}
    </div>
  );

  const customOption = (props) => {
    const { data, innerRef, innerProps, isSelected, isFocused } = props;
    return (
      <div
        ref={innerRef}
        {...innerProps}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px',
          paddingLeft: `${8 + data.level * 15}px`, // Increased indentation for better hierarchy
          backgroundColor: isSelected
            ? '#007bff'
            : isFocused
            ? '#f0f0f0'
            : 'transparent',
          color: isSelected ? 'white' : 'inherit',
          cursor: 'pointer',
          borderRadius: '4px',
          margin: '2px 0',
        }}
      >
        <i
          className={data.icon}
          style={{
            marginRight: 10,
            color: isSelected ? 'white' : '#6c757d', // Icon color
          }}
        />
        {data.label}
      </div>
    );
  };

  const handleChange = (selectedOption) => {
    const sourceTypeChanged = sourceType !== selectedOption.value;
    const agentKeyChanged = selectedOption.agentKey !== agentKey;

    if (sourceTypeChanged) {
      setSourceType(selectedOption.value);
    }

    if (agentKeyChanged) {
      setAgentKey(selectedOption.agentKey || '');
    }
    setSelectedId(selectedOption.id);
    resetSourceTypeChangeFindingsResetAtoms();
  };

  return (
    <Select
      className="custom-360-dropdown text-left mb-2"
      styles={colourStyles}
      options={hostTypeOptions}
      value={
        hostTypeOptions.find((option) => option.id === selectedId) ||
        (agentKey !== ''
          ? hostTypeOptions.find(
              (option) =>
                option.value === sourceType && option.agentKey === agentKey,
            )
          : hostTypeOptions.find((option) => option.value === sourceType))
      }
      onChange={handleChange}
      components={{ SingleValue: customSingleValue, Option: customOption }}
      isSearchable={false}
      getOptionValue={(option) => option.id} // Use the unique ID as the value
    />
  );
};

export default HostTypeDropdown;
